<template>
  <div>
    <v-btn icon color="primary" @click="dialog = true">
      <v-icon>mdi-list-box</v-icon>
    </v-btn>

    <v-dialog v-model="dialog" persistent width="1300">
      <v-card>
        <v-card-title>
          <h2>
            <span class="red--text">JMB</span>
            <span class="blue--text">Computers</span>
          </h2>
          <v-spacer></v-spacer>
          <v-chip
            :color="item.quotation_mode === 'government' ? 'blue' : 'green'"
            dark
            small
            class="mr-3"
          >
            {{
              item.quotation_mode === "government" ? "Government" : "Walk-in"
            }}
          </v-chip>
          <h3 class="display-1">Quote#: {{ item.quote_number }}</h3>
        </v-card-title>
        <v-card-text class="mt-4">
          <div class="d-flex">
            <v-text-field
              outlined
              dense
              readonly
              label="Name"
              prepend-icon="mdi-account"
              :value="item.customer_name"
            ></v-text-field>
            <v-text-field
              outlined
              dense
              readonly
              label="Company Name"
              prepend-icon="mdi-domain"
              :value="item.company_name"
            ></v-text-field>
            <v-text-field
              outlined
              dense
              readonly
              label="Address"
              prepend-icon="mdi-map-marker"
              :value="item.address"
            ></v-text-field>
            <v-text-field
              outlined
              dense
              readonly
              label="Phone Number"
              prepend-icon="mdi-phone"
              :value="item.phone_number"
            ></v-text-field>
          </div>
          <v-row>
            <v-col cols="6">
              <v-textarea
                outlined
                dense
                readonly
                label="Notes"
                height="80"
                :value="item.notes"
                prepend-icon="mdi-draw"
              ></v-textarea>
            </v-col>
            <v-col>
              <v-text-field
                outlined
                dense
                readonly
                label="Quotation Date"
                prepend-icon="mdi-calendar"
                :value="item.quotation_date"
              ></v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                outlined
                dense
                readonly
                label="Expiry Date"
                prepend-icon="mdi-calendar"
                :value="item.quote_expiry_date"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Items Table with Bundle Grouping -->
          <v-simple-table dense class="mt-2">
            <template v-slot:default>
              <thead>
                <tr>
                  <th
                    v-for="h in itemHeaders"
                    :key="h.value"
                    :style="{ width: h.width || 'auto' }"
                  >
                    {{ h.text }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- Standalone items -->
                <tr
                  v-for="(row, idx) in groupedItems.standalone"
                  :key="'s-' + idx"
                >
                  <td class="text-center">{{ idx + 1 }}</td>
                  <td>{{ row.item_type }}</td>
                  <td>{{ row.quantity }}</td>
                  <td>{{ row.unit }}</td>
                  <td>{{ row.item_name }}</td>
                  <td>{{ row.supplier }}</td>
                  <td>{{ formatNum(row.supplier_price) }}</td>
                  <td>
                    <v-icon small :color="row.is_vat ? 'green' : 'grey'">
                      {{ row.is_vat ? "mdi-check" : "mdi-close" }}
                    </v-icon>
                  </td>
                  <td>{{ formatNum(row.freight_in_per_unit) }}</td>
                  <td>{{ formatNum(row.final_supplier_price) }}</td>
                  <td>{{ formatNum(row.final_quote_price_per_unit) }}</td>
                  <td>
                    <strong>{{
                      formatNum(row.final_quote_price_total)
                    }}</strong>
                  </td>
                </tr>

                <!-- Bundle groups -->
                <template v-for="(bundle, bIdx) in groupedItems.bundles">
                  <!-- Bundle Header -->
                  <tr
                    :key="'bh-' + bundle.id"
                    class="teal darken-2 white--text"
                  >
                    <td colspan="12" class="py-2">
                      <strong class="mr-2"
                        >{{
                          groupedItems.standalone.length + bIdx + 1
                        }}.</strong
                      >
                      <v-icon small dark class="mr-1"
                        >mdi-package-variant-closed</v-icon
                      >
                      <strong>{{ bundle.name }}</strong>
                      <v-chip
                        x-small
                        label
                        dark
                        color="teal darken-4"
                        class="ml-2"
                        >{{ bundle.type }}</v-chip
                      >
                      <span class="float-right">
                        Subtotal: {{ formatCurrency(bundle.subtotal) }}
                      </span>
                    </td>
                  </tr>

                  <!-- Bundle Items -->
                  <tr
                    v-for="(row, idx) in bundle.items"
                    :key="'bi-' + bundle.id + '-' + idx"
                    style="border-left: 3px solid #00897b"
                  >
                    <td class="text-center">{{ idx + 1 }}</td>
                    <td>{{ row.item_type }}</td>
                    <td>{{ row.quantity }}</td>
                    <td>{{ row.unit }}</td>
                    <td>{{ row.item_name }}</td>
                    <td>{{ row.supplier }}</td>
                    <td>{{ formatNum(row.supplier_price) }}</td>
                    <td>
                      <v-icon small :color="row.is_vat ? 'green' : 'grey'">
                        {{ row.is_vat ? "mdi-check" : "mdi-close" }}
                      </v-icon>
                    </td>
                    <td>{{ formatNum(row.freight_in_per_unit) }}</td>
                    <td>{{ formatNum(row.final_supplier_price) }}</td>
                    <td>{{ formatNum(row.final_quote_price_per_unit) }}</td>
                    <td>
                      <strong>{{
                        formatNum(row.final_quote_price_total)
                      }}</strong>
                    </td>
                  </tr>
                </template>
              </tbody>
            </template>
          </v-simple-table>

          <hr class="my-4" />

          <!-- Summary -->
          <v-row>
            <v-col cols="12" md="6">
              <v-textarea
                outlined
                dense
                readonly
                height="80"
                :value="item.remarks"
                prepend-icon="mdi-draw"
                label="Remarks"
              ></v-textarea>
              <div class="d-flex mt-2" v-if="can('quotations.view_markup')">
                <div class="mr-5">
                  <strong>Markup:</strong> {{ item.markup_percentage }}%
                </div>
                <div>
                  <strong>VAT Adj:</strong>
                  {{ item.vat_adjustment_percentage }}%
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <v-simple-table dense>
                <tbody>
                  <tr
                    v-if="item.quotation_mode === 'government' && item.abc"
                    class="blue lighten-4"
                  >
                    <td><strong>ABC (Approved Budget for Contract)</strong></td>
                    <td
                      class="
                        text-right
                        font-weight-bold
                        blue--text
                        text--darken-2
                      "
                    >
                      {{ formatCurrency(item.abc) }}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Total Quote Price</strong></td>
                    <td class="text-right primary--text font-weight-bold">
                      {{ formatCurrency(item.total_quote_price || item.total) }}
                    </td>
                  </tr>
                  <tr v-if="can('quotations.view_capital')">
                    <td>Total Capital Expenditure</td>
                    <td class="text-right">
                      {{
                        formatCurrency(
                          item.total_capital_expenditure ||
                            item.total_capital_amount
                        )
                      }}
                    </td>
                  </tr>
                  <tr v-if="item.sop_amount > 0">
                    <td>SOP Amount</td>
                    <td class="text-right">
                      {{ formatCurrency(item.sop_amount) }}
                    </td>
                  </tr>
                  <tr v-if="item.quotation_mode === 'government'">
                    <td>BIR 2307 Withholding (6%)</td>
                    <td class="text-right">
                      {{ formatCurrency(item.bir_2307_amount) }}
                    </td>
                  </tr>
                  <tr
                    v-if="item.quotation_mode === 'government'"
                    class="blue lighten-5"
                  >
                    <td><strong>Cheque Amount</strong></td>
                    <td class="text-right font-weight-bold blue--text">
                      {{ formatCurrency(item.cheque_amount) }}
                    </td>
                  </tr>
                  <tr v-if="item.quotation_mode === 'government'">
                    <td>VAT Payable</td>
                    <td class="text-right">
                      {{ formatCurrency(item.total_vat_payable) }}
                    </td>
                  </tr>
                  <tr
                    v-if="
                      item.quotation_mode === 'government' ||
                      (item.quotation_mode === 'walk_in' &&
                        item.is_vat_purchase)
                    "
                  >
                    <td>Income Tax ({{ item.income_tax_percentage }}%)</td>
                    <td class="text-right">
                      {{ formatCurrency(item.income_tax_amount) }}
                    </td>
                  </tr>
                  <tr
                    v-if="
                      item.quotation_mode === 'walk_in' && item.is_vat_purchase
                    "
                  >
                    <td>VAT (12%)</td>
                    <td class="text-right">
                      {{ formatCurrency(item.vat_amount_walk_in) }}
                    </td>
                  </tr>
                  <tr v-if="item.discount > 0">
                    <td>
                      Discount<span v-if="item.discount_type === 'percentage'">
                        ({{ item.discount_value || 0 }}%)</span
                      >
                    </td>
                    <td class="text-right pink--text text--darken-1">
                      - {{ formatCurrency(item.discount) }}
                    </td>
                  </tr>
                  <tr v-if="item.discount > 0" class="pink lighten-5">
                    <td>
                      <strong>Total After Discount (Customer Pays)</strong>
                    </td>
                    <td
                      class="text-right font-weight-bold pink--text text--darken-1"
                    >
                      {{ formatCurrency(customerTotalAfterDiscount) }}
                    </td>
                  </tr>
                  <tr
                    v-if="can('quotations.view_net_profit')"
                    class="green lighten-5"
                  >
                    <td><strong>Net Profit</strong></td>
                    <td
                      class="text-right font-weight-bold"
                      :class="
                        computedNetProfit >= 0 ? 'green--text' : 'red--text'
                      "
                    >
                      {{ formatCurrency(computedNetProfit) }}
                    </td>
                  </tr>
                  <tr v-if="can('quotations.view_net_profit')">
                    <td><strong>Net Profit Margin</strong></td>
                    <td
                      class="text-right font-weight-bold"
                      :class="
                        computedNetProfitMargin >= 0
                          ? 'green--text'
                          : 'red--text'
                      "
                    >
                      {{ computedNetProfitMargin.toFixed(2) }}%
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="dialog = false">Close</v-btn>
          <v-spacer></v-spacer>
          <p class="caption grey--text">viewQuotationModal.vue</p>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  props: ["item"],
  data() {
    return {
      dialog: false,
      itemHeaders: [
        { text: "#", value: "row_num", width: "40px" },
        { text: "Item Type", value: "item_type", width: "100px" },
        { text: "Qty", value: "quantity", width: "60px" },
        { text: "Unit", value: "unit", width: "60px" },
        { text: "Item Name", value: "item_name" },
        { text: "Supplier", value: "supplier", width: "120px" },
        { text: "Supplier Price", value: "supplier_price", width: "110px" },
        { text: "VAT?", value: "is_vat", width: "60px" },
        { text: "Freight In", value: "freight_in_per_unit", width: "100px" },
        {
          text: "Final Supp. Price",
          value: "final_supplier_price",
          width: "120px",
        },
        {
          text: "Quote $/Unit",
          value: "final_quote_price_per_unit",
          width: "110px",
        },
        { text: "Total", value: "final_quote_price_total", width: "110px" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
    }),
    computedNetProfit() {
      const item = this.item;
      const totalQuotePrice =
        parseFloat(item.total_quote_price || item.total) || 0;
      const totalCapitalExpenditure =
        parseFloat(
          item.total_capital_expenditure || item.total_capital_amount
        ) || 0;
      const sopAmount = parseFloat(item.sop_amount) || 0;

      // Calculate auto-deduct custom deductions (non-capex type)
      let autoDeductCustomDeductions = 0;
      if (Array.isArray(item.custom_deductions)) {
        item.custom_deductions.forEach((ded) => {
          if (ded.deduction_type !== "capex") {
            autoDeductCustomDeductions += parseFloat(ded.computed_amount) || 0;
          }
        });
      }

      const discountAmount = parseFloat(item.discount) || 0;

      if (item.quotation_mode === "government") {
        const totalVatPayable = parseFloat(item.total_vat_payable) || 0;
        const incomeTaxAmount = parseFloat(item.income_tax_amount) || 0;
        return (
          totalQuotePrice -
          totalCapitalExpenditure -
          sopAmount -
          incomeTaxAmount -
          totalVatPayable -
          autoDeductCustomDeductions -
          discountAmount
        );
      } else {
        const incomeTaxAmount = item.is_vat_purchase
          ? parseFloat(item.income_tax_amount) || 0
          : 0;
        return (
          totalQuotePrice -
          totalCapitalExpenditure -
          sopAmount -
          incomeTaxAmount -
          autoDeductCustomDeductions -
          discountAmount
        );
      }
    },
    customerTotalAfterDiscount() {
      const item = this.item;
      const totalQuotePrice =
        parseFloat(item.total_quote_price || item.total) || 0;
      const vatAmount =
        item.quotation_mode === "walk_in" && item.is_vat_purchase
          ? parseFloat(item.vat_amount_walk_in) || 0
          : 0;
      const discountAmount = parseFloat(item.discount) || 0;
      return totalQuotePrice + vatAmount - discountAmount;
    },
    computedNetProfitMargin() {
      const totalQuotePrice =
        parseFloat(this.item.total_quote_price || this.item.total) || 0;
      if (totalQuotePrice === 0) return 0;
      return (this.computedNetProfit / totalQuotePrice) * 100;
    },
    groupedItems() {
      const items = this.item.quote_items || [];
      const standalone = [];
      const bundleMap = {};

      items.forEach((row) => {
        if (row.bundle_id) {
          if (!bundleMap[row.bundle_id]) {
            bundleMap[row.bundle_id] = {
              id: row.bundle_id,
              name: row.bundle_name || "Bundle",
              type: row.bundle_type || "Custom",
              items: [],
              subtotal: 0,
            };
          }
          bundleMap[row.bundle_id].items.push(row);
          bundleMap[row.bundle_id].subtotal +=
            parseFloat(row.final_quote_price_total) || 0;
        } else {
          standalone.push(row);
        }
      });

      // Sort bundle items by sort order
      Object.values(bundleMap).forEach((bundle) => {
        bundle.items.sort(
          (a, b) => (a.bundle_sort_order || 0) - (b.bundle_sort_order || 0)
        );
      });

      return {
        standalone,
        bundles: Object.values(bundleMap),
      };
    },
  },
  methods: {
    // Hat-based permission gate; accounts without hats get no access.
    can(key) {
      return this.hasPermission(key);
    },
    formatNum(val) {
      const num = parseFloat(val) || 0;
      return num.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatCurrency(val) {
      const num = parseFloat(val) || 0;
      return (
        "₱ " +
        num.toLocaleString("en-PH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
  },
};
</script>
